import React from 'react';
import { Box, Stack, alpha } from '@mui/material';
import { useGateway } from 'hooks';
import AnimatedLayout from 'components/layouts/animated-layout';
import PageWithTitleLayout from 'components/layouts/page-with-title/page-with-title-layout';
import {
    OverallFinanceMetrics,
    OverallFinanceChart,
    QuickAccess,
} from 'features/gateways';
import TransactionsAndWithdrawTab from './transactions-and-withdraw-tab';
import HelmetTabComponent from 'components/helmet-tab/helmet-tab';
import PermissionBlur from 'features/gateways/components/permission-blur';
import useGatewayAccess from 'features/gateways/hooks/use-gateway-access';

const GatewayDashboardPage: React.FC = () => {
    const gateway = useGateway();
    const {
        canViewTransactions,
        canViewWithdrawals,
        canEnterSettings,
    } =
        useGatewayAccess();

    const gatewayStatus = (
        <Box
            sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.7,
                py: 0.65,
                px: 1.05,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.success.main, 0.09),
                color: 'success.main',
                fontSize: 10.75,
                fontWeight: 600,
                flexShrink: 0,
            })}>
            <Box
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    boxShadow: (theme) =>
                        `0 0 0 3px ${alpha(theme.palette.success.main, 0.1)}`,
                }}
            />
            درگاه فعال
        </Box>
    );

    return (
        <PageWithTitleLayout
            title={`داشبورد درگاه ${gateway?.title}`}
            slot={gatewayStatus}>
            <HelmetTabComponent title={`داشبورد درگاه ${gateway?.title || ''}`} />

            <AnimatedLayout>
                <Box
                    sx={(theme) => ({
                        position: 'relative',
                        isolation: 'isolate',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            zIndex: -1,
                            width: 260,
                            height: 260,
                            borderRadius: '50%',
                            top: -120,
                            insetInlineEnd: -80,
                            bgcolor: alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.035 : 0.025,
                            ),
                            filter: 'blur(55px)',
                            pointerEvents: 'none',
                        },
                    })}>
                    <Stack spacing={{ xs: 1.5, md: 1.8 }}>
                        <OverallFinanceMetrics />

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'minmax(0, 1fr)',
                                    lg: 'minmax(0, 1.62fr) minmax(330px, 0.78fr)',
                                },
                                gap: { xs: 1.5, md: 1.8 },
                                alignItems: 'stretch',
                            }}>
                            <PermissionBlur
                                allowed={canViewTransactions}
                                label="دسترسی تراکنش‌ها برای شما فعال نیست"
                                borderRadius={3.5}>
                                <OverallFinanceChart
                                    disabled={!canViewTransactions}
                                />
                            </PermissionBlur>

                            <PermissionBlur
                                allowed={canEnterSettings}
                                label="دسترسی تنظیمات این درگاه برای شما فعال نیست"
                                borderRadius={3.5}>
                                <QuickAccess />
                            </PermissionBlur>
                        </Box>

                        <TransactionsAndWithdrawTab
                            showTransaction={canViewTransactions}
                            showWithdraw={canViewWithdrawals}
                        />
                    </Stack>
                </Box>
            </AnimatedLayout>
        </PageWithTitleLayout>
    );
};

export default GatewayDashboardPage;
